import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { FadeUp } from "@/components/site/Reveal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { usePageMeta } from "@/hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Site vitrine",
  "Site avec réservation / rendez-vous",
  "Boutique en ligne",
  "Refonte d'un site existant",
  "Autre projet",
];

const inputCls =
  "w-full rounded-xl bg-white/[0.035] border border-white/[0.09] px-4 py-3.5 text-sm text-white placeholder:text-[var(--text-muted)] outline-none focus:border-violet-500/60 focus:ring-2 focus:ring-violet-500/20 transition-all";

export default function Demande() {
  usePageMeta({
    title: "Demande de projet & aperçu gratuit — Les Amis du Web",
    description:
      "Décrivez votre projet de site internet : nous créons gratuitement un aperçu de votre futur site, adapté à votre activité, sans engagement. Réponse rapide.",
  });
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    company: "",
    activity: "",
    email: "",
    phone: "",
    project_type: "",
    current_website: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.phone || !form.message) {
      toast.error("Merci de remplir tous les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSent(true);
    } catch {
      toast.error("Une erreur est survenue. Réessayez ou écrivez-nous par email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-[var(--bg-primary)]" data-testid="demande-page">
      <Header />
      <main className="relative pt-36 pb-28 overflow-hidden">
        <div className="glow-orb w-[500px] h-[500px] bg-blue-600/15 -top-32 -left-40" />
        <div className="glow-orb w-[420px] h-[420px] bg-violet-600/12 top-1/2 -right-40" />

        <div className="max-w-3xl mx-auto px-6 md:px-10 relative">
          <FadeUp>
            <p className="section-label mb-5">— Demande de projet</p>
          </FadeUp>
          <FadeUp delay={0.08}>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.03]">
              Parlez-nous de <span className="text-gradient">votre projet.</span>
            </h1>
          </FadeUp>
          <FadeUp delay={0.16}>
            <p className="text-[var(--text-secondary)] text-base md:text-lg mt-6 leading-relaxed max-w-xl">
              Décrivez en quelques mots le site internet que vous souhaitez. Une personne compétente dans le domaine
              étudiera votre demande et vous recontactera rapidement — avec un aperçu gratuit de votre futur site.
            </p>
          </FadeUp>

          {sent ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="mt-12 rounded-3xl border border-cyan-400/25 bg-gradient-to-b from-cyan-400/[0.07] to-transparent p-10 text-center"
              data-testid="demande-success"
            >
              <span className="inline-flex w-16 h-16 rounded-full btn-gradient items-center justify-center mb-6">
                <Check size={28} className="text-white" />
              </span>
              <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight">Demande envoyée !</h2>
              <p className="text-[var(--text-secondary)] mt-4 max-w-md mx-auto leading-relaxed">
                Merci pour votre confiance. Nous étudions votre projet et revenons vers vous très rapidement avec un
                premier aperçu gratuit de votre futur site.
              </p>
            </motion.div>
          ) : (
            <FadeUp delay={0.24}>
              <form onSubmit={submit} className="mt-12 rounded-3xl glass p-7 md:p-10 flex flex-col gap-5" data-testid="demande-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-mono-label tracking-[0.15em] uppercase text-[var(--text-muted)] mb-2">Prénom *</label>
                    <input className={inputCls} value={form.first_name} onChange={set("first_name")} placeholder="Votre prénom" data-testid="input-first-name" />
                  </div>
                  <div>
                    <label className="block text-xs font-mono-label tracking-[0.15em] uppercase text-[var(--text-muted)] mb-2">Nom *</label>
                    <input className={inputCls} value={form.last_name} onChange={set("last_name")} placeholder="Votre nom" data-testid="input-last-name" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-mono-label tracking-[0.15em] uppercase text-[var(--text-muted)] mb-2">Entreprise</label>
                    <input className={inputCls} value={form.company} onChange={set("company")} placeholder="Nom de votre entreprise" data-testid="input-company" />
                  </div>
                  <div>
                    <label className="block text-xs font-mono-label tracking-[0.15em] uppercase text-[var(--text-muted)] mb-2">Activité</label>
                    <input className={inputCls} value={form.activity} onChange={set("activity")} placeholder="Ex : restaurant, artisan, cabinet…" data-testid="input-activity" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-mono-label tracking-[0.15em] uppercase text-[var(--text-muted)] mb-2">Adresse email *</label>
                    <input type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="vous@exemple.fr" data-testid="input-email" />
                  </div>
                  <div>
                    <label className="block text-xs font-mono-label tracking-[0.15em] uppercase text-[var(--text-muted)] mb-2">Téléphone *</label>
                    <input className={inputCls} value={form.phone} onChange={set("phone")} placeholder="06 12 34 56 78" data-testid="input-phone" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-mono-label tracking-[0.15em] uppercase text-[var(--text-muted)] mb-2">Type de projet</label>
                    <Select value={form.project_type} onValueChange={(v) => setForm({ ...form, project_type: v })}>
                      <SelectTrigger className="w-full rounded-xl bg-white/[0.035] border border-white/[0.09] px-4 h-[50px] text-sm text-white data-[placeholder]:text-[var(--text-muted)]" data-testid="select-project-type">
                        <SelectValue placeholder="Choisir un type de projet" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#12132a] border-white/[0.1] text-white">
                        {PROJECT_TYPES.map((t) => (
                          <SelectItem key={t} value={t} data-testid={`project-type-${t}`}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs font-mono-label tracking-[0.15em] uppercase text-[var(--text-muted)] mb-2">Site internet actuel</label>
                    <input className={inputCls} value={form.current_website} onChange={set("current_website")} placeholder="https://… (si vous en avez un)" data-testid="input-current-website" />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-mono-label tracking-[0.15em] uppercase text-[var(--text-muted)] mb-2">Votre projet en quelques mots *</label>
                  <textarea
                    rows={5}
                    className={`${inputCls} resize-none`}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Décrivez votre activité, vos envies, vos mots-clés pour le référencement…"
                    data-testid="input-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-gradient inline-flex items-center justify-center gap-2.5 rounded-full px-8 py-4 font-semibold text-white disabled:opacity-60 mt-2"
                  data-testid="submit-demande-btn"
                >
                  {loading ? <Loader2 size={17} className="animate-spin" /> : <ArrowRight size={17} />}
                  {loading ? "Envoi en cours…" : "Envoyer ma demande"}
                </button>
                <p className="text-xs text-[var(--text-muted)] text-center">
                  Gratuit et sans engagement — vous voyez le résultat avant de décider.
                </p>
              </form>
            </FadeUp>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
